import { useEffect, useState } from "react";
import {
  Checkbox,
  Toast,
  Toaster,
  ToastTitle,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { Button } from "../../../components/Button";
import TextEditor from "../../components/TextEditor";
import { useStyles } from "./LandingPage.styles";
import Header from "../../components/Header";
import { useApi } from "../../../hooks";
import {
  createConfiguration,
  getComponentMasters,
  getConfiguration,
  getInputCategoryMasters,
  getKpiMasters,
  getScreenMasters,
} from "../../../services/adminService";
import {
  IComponentMaster,
  IInputCategoryMaster,
  IKpiMaster,
  IScreenMaster,
} from "./LandingPageProps";
import Dropdown, {
  IDropdownOption,
  mapToOptions,
} from "../../../components/CustomDropdown";
import Container from "../../../components/Container";

const initialState = {
  page: "",
  component: "",
  kpi: "",
  inputCategory: "",
  isAppear: false,
};

const LandingPage = () => {
  const styles = useStyles();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  // API calls
  const { data: screenMaster } = useApi<IScreenMaster[]>(getScreenMasters);
  const { data: componentMaster } =
    useApi<IComponentMaster[]>(getComponentMasters);
  const { data: inputCategoryMaster } = useApi<IInputCategoryMaster[]>(
    getInputCategoryMasters
  );
  const { data: kpiMaster } = useApi<IKpiMaster[]>(getKpiMasters);
  const { data: saveConfigResponse, execute } = useApi(createConfiguration, {
    autoFetch: false,
  });
  const { data: configResponse, execute: getConfigurationCall } = useApi(
    getConfiguration,
    {
      autoFetch: false,
    }
  );

  // Dropdown options
  const [screenOptions, setScreenOptions] = useState<IDropdownOption[]>([]);
  const [componentOptions, setComponentOptions] = useState<IDropdownOption[]>(
    []
  );
  const [inputCategoryOptions, setInputCategoryOptions] = useState<
    IDropdownOption[]
  >([]);
  const [kpiOptions, setKpiOptions] = useState<IDropdownOption[]>([]);

  // Form state
  const [showKpiDropdown, setShowKpiDropdown] = useState(false);
  const [formState, setFormState] = useState(initialState);
  const [editorContent, setEditorContent] = useState<string>("");

  useEffect(() => {
    const { page, component, inputCategory, kpi } = formState;

    if (page && component && inputCategory) {
      const paylaod = {
        screenId: page,
        componentId: component,
        inputCategoryId: inputCategory,
        kpiId: kpi?.length ? kpi : 0,
      };

      getConfigurationCall(paylaod);
    }

    //eslint-disable-next-line
  }, [formState]);

  useEffect(() => {
    if (saveConfigResponse) {
      dispatchToast(
        <Toast>
          <ToastTitle>{saveConfigResponse.message}</ToastTitle>
        </Toast>,
        { intent: "success" }
      );
    }

    //eslint-disable-next-line
  }, [saveConfigResponse, configResponse]);

  useEffect(() => {
    if (configResponse) {
      setEditorContent(configResponse?.contentDetails);
    }
  }, [configResponse]);

  useEffect(() => {
    if (screenMaster?.length) {
      const options = mapToOptions(screenMaster, "id", "screenName");
      setScreenOptions(options);
    }

    if (componentMaster?.length) {
      const options = mapToOptions(componentMaster, "id", "componentName");
      setComponentOptions(options);
    }

    if (inputCategoryMaster?.length) {
      const options = mapToOptions(inputCategoryMaster, "id", "categoryName");
      setInputCategoryOptions(options);
    }

    if (kpiMaster?.length) {
      const options = mapToOptions(kpiMaster, "id", "kpiName");
      setKpiOptions(options);
    }
  }, [screenMaster, componentMaster, inputCategoryMaster, kpiMaster]);

  const handleSave = () => {
    const payload = {
      screenId: +formState.page,
      componentId: +formState.component,
      kpiId: +formState.kpi,
      inputCategoryId: +formState.inputCategory,
      contentDetails: editorContent,
      isAppear: formState.isAppear,
      isPublish: false,
    };

    execute(payload);
  };

  return (
    <>
      <Header />

      <Container className={styles.container}>
        <div className={styles.dropdownContainer}>
          <Dropdown
            id="pageDropdown"
            label="Page"
            options={screenOptions}
            value={formState.page}
            handleChange={(key) =>
              setFormState((prev) => ({ ...prev, page: key }))
            }
          />

          <Dropdown
            id="componentDropdown"
            label="Component"
            options={componentOptions}
            value={formState.component}
            handleChange={(key) => {
              setFormState((prev) => ({ ...prev, component: key }));

              const hasKpi =
                componentMaster?.find((c) => c.id === Number(key))?.hasKpi ??
                false;
              setShowKpiDropdown(hasKpi);
            }}
          />

          {showKpiDropdown && (
            <Dropdown
              id="kpiDropdown"
              label="KPI"
              options={kpiOptions}
              value={formState.kpi}
              handleChange={(key) =>
                setFormState((prev) => ({ ...prev, kpi: key }))
              }
            />
          )}

          <Dropdown
            id="inputCategoryDropdown"
            label="Input Category"
            options={inputCategoryOptions}
            value={formState.inputCategory}
            handleChange={(key) =>
              setFormState((prev) => ({ ...prev, inputCategory: key }))
            }
          />

          {showKpiDropdown && (
            <Checkbox
              label="Is Appear"
              name="isAppear"
              checked={formState.isAppear}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  isAppear: event.target.checked,
                }))
              }
            />
          )}
        </div>

        <div>
          <TextEditor
            editorData={editorContent}
            setEditorData={setEditorContent}
          />
        </div>

        <div className={styles.buttonContainer}>
          <Button label="Save" onClick={handleSave} />
          <Button label="Preview" />
          <Button label="Publish" />
        </div>
      </Container>

      <Toaster toasterId={toasterId} />
    </>
  );
};

export default LandingPage;
